import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Icons,
  iconSize,
  MenuItem,
  palette,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { HAZARD_KINDS, type HazardKind, type PropertyHazardView } from '@munifocus/contracts-gov';
import { useMutationToast } from '../../lib/mutation-toast';
import { useClearHazard, useCreateHazard, useHazards, useUpdateHazard } from '../../lib/safety-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

function formatDate(iso: string, language: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
    }).format(new Date(iso)),
  );
}

interface Draft {
  kind: HazardKind;
  note: string;
  reviewOn: string;
}

function HazardDialog({
  title,
  initial,
  open,
  saving,
  onClose,
  onSave,
}: {
  title: string;
  initial: Draft;
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: Draft) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(initial);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            select
            label={t('gov.safety.hazards.kind')}
            value={draft.kind}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value as HazardKind })}
            fullWidth
          >
            {HAZARD_KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`gov.safety.hazards.kinds.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('gov.safety.hazards.note')}
            placeholder={t('gov.safety.hazards.notePlaceholder')}
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
          <TextField
            type="date"
            label={t('gov.safety.hazards.reviewOn')}
            helperText={t('gov.safety.hazards.reviewOnHint')}
            value={draft.reviewOn}
            onChange={(e) => setDraft({ ...draft, reviewOn: e.target.value })}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" disabled={saving} onClick={() => onSave(draft)}>
          {t('gov.safety.hazards.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ClearDialog({
  hazard,
  saving,
  onClose,
  onClear,
}: {
  hazard: PropertyHazardView | null;
  saving: boolean;
  onClose: () => void;
  onClear: (reason: string) => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog open={!!hazard} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('gov.safety.hazards.clearTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t('gov.safety.hazards.clearBody')}</DialogContentText>
        <TextField
          label={t('gov.safety.hazards.clearReason')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" color="error" disabled={saving} onClick={() => onClear(reason)}>
          {t('gov.safety.hazards.clear')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EMPTY: Draft = { kind: 'aggressive_dog', note: '', reviewOn: '' };

/**
 * Hazard flags on a file: every active warning at the property (its own and
 * those set on other files at the same PID), who set each and when, and its
 * review date. Officers working the file (`file:update`) add, edit and clear
 * them; everyone who can read the file sees them.
 */
export function HazardPanel({ fileId, canEdit }: { fileId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const toast = useMutationToast();
  const { data, isLoading, isError } = useHazards(fileId);
  const create = useCreateHazard(fileId);
  const update = useUpdateHazard(fileId);
  const clear = useClearHazard(fileId);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<PropertyHazardView | null>(null);
  const [clearing, setClearing] = useState<PropertyHazardView | null>(null);

  if (data && !data.enabled) return null;

  const done = (key: string, close: () => void) => ({
    onSuccess: () => {
      toast.success(key);
      close();
    },
    onError: (err: unknown) => toast.error(err, 'gov.safety.hazards.failed'),
  });

  return (
    <Box component="section" aria-labelledby="hazards-title">
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Icons.AlertTriangle size={iconSize.md} aria-hidden />
          <Typography id="hazards-title" variant="overline">
            {t('gov.safety.hazards.title')}
          </Typography>
        </Stack>
        {canEdit && (
          <Button
            size="small"
            startIcon={<Icons.Plus size={iconSize.sm} aria-hidden />}
            onClick={() => setAdding(true)}
          >
            {t('gov.safety.hazards.add')}
          </Button>
        )}
      </Stack>

      {isLoading && <CircularProgress size={20} />}
      {isError && <Alert severity="error">{t('gov.safety.hazards.loadError')}</Alert>}
      {data && data.hazards.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('gov.safety.hazards.empty')}
        </Typography>
      )}

      <Stack component="ul" spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {data?.hazards.map((h) => (
          <Box
            component="li"
            key={h.id}
            sx={{ pl: 1.5, borderLeft: `3px solid ${palette.semantic.emergency}` }}
          >
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              <Typography sx={{ fontWeight: 700 }}>
                {t(`gov.safety.hazards.kinds.${h.kind}`)}
              </Typography>
              {h.reviewDue && (
                <StatusChip status="warning" label={t('gov.safety.hazards.reviewDue')} />
              )}
            </Stack>
            {h.note && <Typography variant="body2">{h.note}</Typography>}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {h.createdByName
                ? t('gov.safety.hazards.setBy', {
                    name: h.createdByName,
                    date: formatDate(h.createdAt, i18n.language),
                  })
                : t('gov.safety.hazards.setByUnknown', {
                    date: formatDate(h.createdAt, i18n.language),
                  })}
              {h.reviewOn ? ` · ${t('gov.safety.hazards.reviewOn')} ${h.reviewOn}` : ''}
            </Typography>
            {h.inherited && h.sourceFileReference && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('gov.safety.hazards.inherited', { reference: h.sourceFileReference })}
              </Typography>
            )}
            {canEdit && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Button size="small" onClick={() => setEditing(h)}>
                  {t('gov.safety.hazards.edit')}
                </Button>
                <Button size="small" color="error" onClick={() => setClearing(h)}>
                  {t('gov.safety.hazards.clear')}
                </Button>
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {adding && (
        <HazardDialog
          open
          title={t('gov.safety.hazards.add')}
          initial={EMPTY}
          saving={create.isPending}
          onClose={() => setAdding(false)}
          onSave={(d) =>
            create.mutate(
              { kind: d.kind, note: d.note.trim(), reviewOn: d.reviewOn || null },
              done('gov.safety.hazards.saved', () => setAdding(false)),
            )
          }
        />
      )}
      {editing && (
        <HazardDialog
          open
          title={t('gov.safety.hazards.edit')}
          initial={
            editing
              ? { kind: editing.kind, note: editing.note, reviewOn: editing.reviewOn ?? '' }
              : EMPTY
          }
          saving={update.isPending}
          onClose={() => setEditing(null)}
          onSave={(d) =>
            editing &&
            update.mutate(
              {
                hazardId: editing.id,
                kind: d.kind,
                note: d.note.trim(),
                reviewOn: d.reviewOn || null,
              },
              done('gov.safety.hazards.saved', () => setEditing(null)),
            )
          }
        />
      )}
      <ClearDialog
        key={clearing?.id ?? 'none'}
        hazard={clearing}
        saving={clear.isPending}
        onClose={() => setClearing(null)}
        onClear={(reason) =>
          clearing &&
          clear.mutate(
            { hazardId: clearing.id, reason: reason.trim() || undefined },
            done('gov.safety.hazards.cleared', () => setClearing(null)),
          )
        }
      />
    </Box>
  );
}
